import React from "react";
import { GoldDivider } from "@/components/luxury";

export default function TheArmstrongK9Standard() {
  return (
    <>
      <p className="font-spectral italic text-xl md:text-2xl tracking-tight text-[#3a3a3a] leading-snug max-w-2xl">
        Armstrong K9 Registry is a venue, not a regulator. We don't tell breeders
        how to breed. But we tell buyers — in writing, with no
        asterisks — exactly what "verified" means here.
      </p>

      <GoldDivider variant="ornament" className="max-w-md my-10" />

      <p>
        Most online dog registries either (a) gate-keep aggressively and
        anoint a small number of "approved" bloodlines, or (b) take all
        comers and call themselves "open." Both miss the point.
      </p>
      <p>
        Buyers who care don't need us to pick winners. They need a
        platform that surfaces the things they'd verify themselves if
        they had the time and the access — pedigree depth, microchip
        uniqueness, identity of the kennel, history of completed
        transfers, plausibility of the breeding cadence — and gets out
        of the way.
      </p>

      <h2>What "verified" means here</h2>
      <p>
        We use a single word — <em>verified</em> — and we're careful
        with it. A verified kennel on Armstrong K9 has been through the{" "}
        <a href="/journal/how-we-verify-a-kennel" className="text-ink underline underline-offset-4 hover:text-crimson">
          seven-point review
        </a>{" "}
        and is wearing the gold WaxSeal because a human signed off. That
        is the only thing the seal means. It is not an endorsement of
        any specific breeding decision the kennel makes.
      </p>
      <p>
        If you see a verified kennel doing something you wouldn't —
        breeding back-to-back, hand-selling at six weeks, working with a
        breed you consider unethical — that's a disagreement between
        you and them about how to breed, not a failure on our part to
        verify. We won't litigate breeding philosophy, and we won't
        pretend that a kennel registry is the right venue to do it.
      </p>

      <h2>What the platform refuses to do</h2>
      <p>
        We will not run a "best breeders" leaderboard, run a paid
        ranking, or sell premium placement based on anything other than
        the editorial value of a feature. We don't take money from
        kennels in exchange for seals or seal-priority. The verification
        fee, when we charge it, covers our own ops cost and nothing
        more.
      </p>
      <p>
        We will not arbitrate breeding ethics. If you think a kennel is
        in violation of an actual law (welfare, identification, false
        advertising), report them to the relevant authority and tell us
        — we'll cooperate fully and remove their seal pending the
        outcome. But we will not be the venue where a breed-internal
        ethical fight gets adjudicated.
      </p>
      <p>
        We will not auto-list dogs. Every animal on the marketplace is
        manually listed by a registered kennel or owner. There is no
        scraping, no third-party API, no "powered by." A dog appears
        because a person took the time to enter it.
      </p>

      <h2>What buyers can rely on</h2>
      <p>
        Three things, every time:
      </p>
      <ul>
        <li>
          <strong>The pedigree on a dog's page is the pedigree the
          kennel filed</strong> — not a synthesised guess. If the
          ancestors are missing, you'll see them as missing, and you'll
          see the date the gap was last edited.
        </li>
        <li>
          <strong>The microchip on the dog's record is unique on this
          platform.</strong> No other dog on Armstrong K9 carries the
          same chip number. If two records ever did, the second one
          could not be saved.
        </li>
        <li>
          <strong>The "verified" seal means seven things.</strong>{" "}
          Always the same seven. A verified kennel today is the same
          standard as a verified kennel a year ago.
        </li>
      </ul>

      <GoldDivider variant="ornament" className="max-w-md my-10" />

      <p>
        Everything else — what breeds we host, what prices we tolerate,
        which kennels rise to the top of search — is a buyer's call to
        make with their own judgement. The platform's job is to give
        them the receipts to make it.
      </p>
    </>
  );
}
